<template>
  <a-dialog
      ref="dialog"
      size="large"
      :persistent="false"
      :toolbar="true"
      :actions="false"
      dense="mobile"
      name="keyword_detail"
      :progress="progress"
      :toolbar-tab="[$t('detail'), $t('evolution')]"
      @toolbar-tab-changed="onToolbarTabChanged"
      @open="opened"
      @close="$reset">
    <span slot="title" v-if="monitor">
      {{ texts.term }}
      (<geo-lang-name size="sm" :country="texts.country" :language="texts.language"></geo-lang-name>)
      - {{ texts.domain }}
    </span>
    <div slot="content" class="mt-4">
      <v-tabs-items v-model="tab.active">
        <v-tab-item :transition="false">
          <v-row class="mb-2" dense>
            <v-col cols="12" md="4" :order="this.$vuetify.breakpoint.smAndDown ? 'last' : 'first'">
              <v-btn :href="serp.current ? serp.current.page().url : ''" :disabled="!serp.current" target="_blank"
                     depressed small>
                <v-icon small left>mdi-open-in-new</v-icon>
                {{ $t('actions.view') }}
              </v-btn>
            </v-col>
            <v-col :class="this.$vuetify.breakpoint.smAndDown ? '' : 'text-right'" cols="12" md="8"
                   :order="this.$vuetify.breakpoint.smAndDown ? 'first' : 'last'">
              <date-picker
                  :class="this.$vuetify.breakpoint.smAndDown ? '' : 'float-right'"
                  @changed="handleDatePickerChanged"
                  :currentDate="datepicker.current"
                  :comparisonDate="datepicker.comparison">
              </date-picker>
            </v-col>
          </v-row>

          <div class="mt-4 mb-4" v-if="todayNotes.length > 0">
            <v-alert type="info" text shaped icon="mdi-comment-text-multiple-outline">
              <div class="font-weight-bold mb-2">{{ $t('notes-of') }} {{ $moment(datepicker.current).format('ll') }} :
              </div>
              <div v-for="(note, i) in todayNotes" :key="i">
                <v-edit-dialog
                    @save="$repos.notes.update(note.id, {description: note.description})"
                    large
                    :cancel-text="$t('actions.cancel')"
                    :save-text="$t('actions.save')">
                  {{ note.description }}
                  <edit-dialog-pencil color="info"></edit-dialog-pencil>
                  <template v-slot:input>
                    <v-textarea
                        rows="3"
                        v-model="note.description"
                        :counter="1000"
                        auto-grow
                    ></v-textarea>
                  </template>
                </v-edit-dialog>
              </div>
            </v-alert>
          </div>

          <serp-dialog-detail-table
              :style="'display: ' + (tab.active === 0 ? 'block' : 'none')"
              :serp="serp.current"
              :comparison="serp.comparison">
          </serp-dialog-detail-table>
        </v-tab-item>
        <v-tab-item :transition="false">
          <keyword-evolution-chart
              :style="'display: ' + (tab.active === 1 ? 'block' : 'none')"
              :monitor="monitor">
          </keyword-evolution-chart>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </a-dialog>
</template>

<script>
import KeywordEvolutionChart from './Components/KeywordEvolutionChart'
import SerpDialogDetailTable from '@/views/Common/Components/SerpDetail/SerpDialogDetailTable'
import DatePicker from '@/views/Pages/PositionMonitoring/Components/Common/DatePicker'
import GeoLangName from '@/views/Common/Components/GeoLang/GeoLangName'
import EditDialogPencil from '@/views/Common/Components/Table/EditDialogPencil'

export default {
  name: 'KeywordDetailDialog',
  components: { EditDialogPencil, GeoLangName, DatePicker, SerpDialogDetailTable, KeywordEvolutionChart },
  data () {
    return {
      progress: false,
      carrier: {},
      monitor: null,
      texts: {
        term: '',
        country: '',
        language: '',
        domain: ''
      },
      datepicker: {
        current: null,
        comparison: null,
      },
      serp: {
        loading: true,
        current: null,
        comparison: null
      },
      tab: {
        active: 0
      },
    }
  },
  computed: {
    mobile () {
      return this.$vuetify.breakpoint.smAndDown
    },
    todayNotes () {
      if (this.monitor && this.monitor.notes) {
        return this.monitor.notes.filter(note => {
          return this.$moment(note.at).isSame(this.datepicker.current, 'day')
        })
      } else {
        return []
      }
    }
  },
  methods: {
    onToolbarTabChanged (value) {
      this.tab.active = value
    },
    async handleDatePickerChanged ({ current, comparison }) {
      this.datepicker.current = current
      this.datepicker.comparison = comparison

      await this.fetch()
    },
    async fetch () {
      try {
        this.progress = true

        try {
          const path = this.$models.CarrierObjects.monitorSerp(this.monitor, this.datepicker.current)
          const current = (await this.$carrier.get(path)).data
          this.serp.current = this.$models.serp.get(current)
        } catch (e) {
          console.warn(e)
          this.serp.current = false
        }

        try {
          const path = this.$models.CarrierObjects.monitorSerp(this.monitor, this.datepicker.comparison)
          const comparison = (await this.$carrier.get(path)).data
          this.serp.comparison = this.$models.serp.get(comparison)
        } catch (e) {
          console.warn(e)
          this.serp.comparison = false
        }
      } finally {
        this.progress = false
      }
    },
    async opened ({ item, date }) {
      this.texts.term = item.term
      this.texts.country = item.country
      this.texts.language = item.language
      this.texts.domain = item.domain

      const result = await this.$api.get('monitors', {
        where: { id: item.id },
        with: ['keyword', 'website', 'notes'],
        limit: 1
      }, { showLoading: false })

      this.monitor = result.data[0]

      this.datepicker.current = date.current
      this.datepicker.comparison = date.comparison

      await this.fetch()
    }
  }
}
</script>

<style>
.text-right div {
  text-align: right;
}
</style>

<i18n>
{
	"en": {
		"detail": "Detail",
		"evolution": "Evolution",
		"notes-of": "Note(s) of"
	},
	"es": {
		"detail": "Detalle",
		"evolution": "Evolución",
		"notes-of": "Nota(s) a"
	},
	"fr": {
		"detail": "Détail",
		"evolution": "Évolution",
		"notes-of": "Note(s) au"
	},
	"it": {
		"detail": "Dettaglio",
		"evolution": "Evoluzione",
		"notes-of": "Nota/e del"
	},
	"pt": {
		"detail": "Detalhe",
		"evolution": "Evolução",
		"notes-of": "Nota(s) z"
	},
	"pl": {
		"detail": "Szczegóły",
		"evolution": "Ewolucja",
		"notes-of": "Notatka(-i) z"
	}
}
</i18n>
